import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import PropTypes from 'prop-types'
import SectionDivider from '../../Components/Divider/SectionDivider'
import React, { useEffect, useState } from 'react'
import CollectorStatsCards from './CollectorStatsCards'
import { Button } from 'react-admin'
import RulesAccordion from './RulesAccordion'

const CollectorDetails = ({
  selectedZone,
  collectorSheetResponse,
  zonesReportResponse,
}) => {
  const [open, setOpen] = React.useState(true)
  const [weightBracket, setWeightBracket] = useState(
    'collection_agent_801_1100'
  )

  const weightBrackets = [
    { value: 'collection_agent_0_800', label: 'Up to 800' },
    { value: 'collection_agent_801_1100', label: '801 - 1100' },
    { value: 'collection_agent_1101_1250', label: '1101 - 1250' },
    { value: 'collection_agent_1251_1400', label: '1251 - 1400' },
    { value: 'collection_agent_1400', label: '1400+' },
  ]

  const postcodes = collectorSheetResponse?.map((item) => item.postcode)

  const isHighestPrice = (postcode, weightBracket, currentPrice) => {
    const matchingZones = zonesReportResponse.zones.filter((zone) =>
      zone.postcodes_covered.split(',').includes(postcode.toUpperCase())
    )

    const prices = matchingZones.map(
      (zone) => parseFloat(zone[weightBracket]) || 0
    )
    const maxPrice = Math.max(...prices)

    return parseFloat(currentPrice) >= maxPrice
  }

  const getTopCollectors = () => {
    return postcodes?.map((postcode) => {
      // Filter zones that cover this postcode
      const matchingZones = zonesReportResponse.zones.filter((zone) =>
        zone.postcodes_covered.split(',').includes(postcode.toUpperCase())
      )

      // Sort by selected weight bracket (highest first)
      const sortedZones = [...matchingZones].sort(
        (a, b) => parseFloat(b[weightBracket]) - parseFloat(a[weightBracket])
      )

      // Extract top 6 companies
      const topCollectors = []
      for (let i = 0; i < 6; i++) {
        topCollectors.push(sortedZones[i]?.company_name || '-')
      }

      return {
        postcode,
        collectors: topCollectors,
      }
    })
  }
  const results = getTopCollectors()

  useEffect(() => {
    setOpen(true)
  }, [collectorSheetResponse])

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="xl"
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle>{selectedZone.company_name}</DialogTitle>
        <DialogContent>
          <SectionDivider
            title={'Collector Sheet - ' + selectedZone.company_name}
            sx={{ mt: 2, textAlign: 'center' }}
          />
          <CollectorStatsCards selectedZone={selectedZone} />
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '15%' }}>Postcode</TableCell>
                <TableCell>up to 800KG</TableCell>
                <TableCell>801KG - 1100KG</TableCell>
                <TableCell>1101KG - 1250KG</TableCell>
                <TableCell>1251KG - 1400KG</TableCell>
                <TableCell>1400KG+</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {collectorSheetResponse?.map((record) => (
                <TableRow key={record.postcode}>
                  <TableCell>{record.postcode}</TableCell>
                  <TableCell
                    style={{
                      backgroundColor: isHighestPrice(
                        record.postcode,
                        'collection_agent_0_800',
                        record.collection_agent_0_800
                      )
                        ? 'orange'
                        : 'lightgreen',
                    }}
                  >
                    £{record.collection_agent_0_800}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: isHighestPrice(
                        record.postcode,
                        'collection_agent_801_1100',
                        record.collection_agent_801_1100
                      )
                        ? 'orange'
                        : 'lightgreen',
                    }}
                  >
                    £{record.collection_agent_801_1100}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: isHighestPrice(
                        record.postcode,
                        'collection_agent_1101_1250',
                        record.collection_agent_1101_1250
                      )
                        ? 'orange'
                        : 'lightgreen',
                    }}
                  >
                    £{record.collection_agent_1101_1250}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: isHighestPrice(
                        record.postcode,
                        'collection_agent_1251_1400',
                        record.collection_agent_1251_1400
                      )
                        ? 'orange'
                        : 'lightgreen',
                    }}
                  >
                    £{record.collection_agent_1251_1400}
                  </TableCell>
                  <TableCell
                    style={{
                      backgroundColor: isHighestPrice(
                        record.postcode,
                        'collection_agent_1400',
                        record.collection_agent_1400
                      )
                        ? 'orange'
                        : 'lightgreen',
                    }}
                  >
                    £{record.collection_agent_1400}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <SectionDivider
            title={'Leading competitors'}
            sx={{ mt: 2, textAlign: 'center' }}
          />
          <div>
            <FormControl sx={{ m: 2, minWidth: 200 }}>
              <InputLabel>Weight Bracket</InputLabel>
              <Select
                value={weightBracket}
                onChange={(e) => setWeightBracket(e.target.value)}
                label="Weight Bracket"
              >
                {weightBrackets.map((bracket) => (
                  <MenuItem key={bracket.value} value={bracket.value}>
                    {bracket.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Postcode</TableCell>
                    <TableCell>Top Collector</TableCell>
                    <TableCell>2nd Competitor</TableCell>
                    <TableCell>3rd Competitor</TableCell>
                    <TableCell>4th Competitor</TableCell>
                    <TableCell>5th Competitor</TableCell>
                    <TableCell>6th Competitor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.postcode}</TableCell>
                      {row.collectors.map((collector, i) => {
                        let cellStyle = {}
                        if (
                          i === 0 &&
                          collector === selectedZone.company_name
                        ) {
                          cellStyle.backgroundColor = 'orange'
                        } else if (i === 0) {
                          cellStyle.backgroundColor = 'lightgreen'
                        } else if (collector === selectedZone.company_name) {
                          cellStyle.backgroundColor = 'orange'
                        }

                        return (
                          <TableCell key={i} style={cellStyle}>
                            {collector}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <RulesAccordion
              collectionAgentId={selectedZone.collection_agent_id}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

CollectorDetails.propTypes = {
  // title: PropTypes.string,
  selectedZone: PropTypes.object,
  collectorSheetResponse: PropTypes.object,
  zonesReportResponse: PropTypes.object,
}

export default CollectorDetails
